import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  IndianRupee,
  LogIn,
  LogOut,
  MessageSquare,
  Phone,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Settings,
  Shield,
  Trash2,
  TrendingUp,
  Upload,
  UserPlus,
  X,
  XCircle,
} from "lucide-react";
import API from "../../lib/api";

const PAGE_SIZE = 25;

// Action label / icon / tone metadata. Unknown actions fall back to a neutral
// gray Activity icon — see metaForAction below.
const LOG_ACTION_META = {
  USER_CREATE:           { label: "User Created",        icon: UserPlus,    tone: "create" },
  USER_ROLE_UPDATE:      { label: "User Role Changed",   icon: Shield,      tone: "update" },
  USER_STATUS_UPDATE:    { label: "User Status Changed", icon: Settings,    tone: "update" },
  USER_CA_ASSIGN:        { label: "CA Assigned to User", icon: UserPlus,    tone: "update" },
  ORDER_STATUS_UPDATE:   { label: "Order Status Changed",icon: Settings,    tone: "status" },
  STEP_COMPLETE:         { label: "Step Completed",      icon: CheckCircle2,tone: "create" },
  STEP_REOPEN:           { label: "Step Reopened",       icon: RefreshCw,   tone: "update" },
  COMPUTATION_UPDATE:    { label: "Computation Updated", icon: TrendingUp,  tone: "update" },
  DELIVERABLE_UPLOAD:    { label: "Deliverable Uploaded",icon: Upload,      tone: "upload" },
  CYCLE_DELIVERABLE_UPLOAD: { label: "Cycle Deliverable Uploaded", icon: Upload, tone: "upload" },
  CYCLE_UPDATE:          { label: "Cycle Updated",       icon: RefreshCw,   tone: "update" },
  NOTE_ADD:              { label: "Note Added",          icon: MessageSquare,tone: "create" },
  CA_PROFILE_CREATE:     { label: "CA Profile Created",  icon: Plus,        tone: "create" },
  CA_PROFILE_UPDATE:     { label: "CA Profile Updated",  icon: Settings,    tone: "update" },
  CA_PROFILE_DELETE:     { label: "CA Profile Deleted",  icon: Trash2,      tone: "delete" },
  TEAM_CREATE:           { label: "Team Member Added",   icon: UserPlus,    tone: "create" },
  TEAM_UPDATE:           { label: "Team Member Updated", icon: Settings,    tone: "update" },
  TEAM_STATUS_TOGGLE:    { label: "Team Status Toggled", icon: Settings,    tone: "status" },
  PAYMENT_CAPTURED:      { label: "Payment Captured",    icon: IndianRupee, tone: "payment" },
  PAYMENT_REFUNDED:      { label: "Payment Refunded",    icon: IndianRupee, tone: "delete" },
  CONTACT_REPLY:         { label: "Contact Replied",     icon: MessageSquare,tone: "create" },
  CALL_REQUEST_UPDATE:   { label: "Call Request Updated",icon: Phone,       tone: "update" },
  LOGIN:                 { label: "Admin Login",         icon: LogIn,       tone: "login" },
  LOGOUT:                { label: "Admin Logout",        icon: LogOut,      tone: "login" },
  LOGIN_FAILURE:         { label: "Login Failed",        icon: XCircle,     tone: "delete" },
  USER_REGISTER:         { label: "User Registered",     icon: UserPlus,    tone: "create" },
  USER_LOGIN:            { label: "User Login",          icon: LogIn,       tone: "login" },
  USER_LOGOUT:           { label: "User Logout",         icon: LogOut,      tone: "login" },
  USER_LOGIN_FAILURE:    { label: "User Login Failed",   icon: XCircle,     tone: "delete" },
  USER_PROFILE_UPDATE:   { label: "User Updated Profile",icon: Settings,    tone: "update" },
  ORDER_CREATE:          { label: "Order Created",       icon: Plus,        tone: "create" },
  USER_STEP_COMPLETE:    { label: "User Completed Step", icon: CheckCircle2,tone: "create" },
  USER_DOCUMENT_UPLOAD:  { label: "User Uploaded Document", icon: Upload,   tone: "upload" },
  ITR_SUBMIT:            { label: "ITR Submitted",       icon: Upload,      tone: "create" },
  CALL_REQUEST_CREATE:   { label: "Call Booked",         icon: Phone,       tone: "create" },
  PAYMENT_SIGNATURE_INVALID: { label: "Invalid Payment Signature", icon: XCircle, tone: "delete" },
  CONTACT_SUBMIT:        { label: "Contact Submitted",   icon: MessageSquare,tone: "create" },
  DOCUMENT_DOWNLOAD:     { label: "Document Downloaded", icon: Download,    tone: "update" },
  DOCUMENT_PREVIEW:      { label: "Document Previewed",  icon: Search,      tone: "update" },
  DRAFT_UPLOAD:          { label: "Draft Uploaded",      icon: Upload,      tone: "upload" },
  DRAFT_RESUBMIT:        { label: "Draft Resubmitted",   icon: Upload,      tone: "upload" },
  DRAFT_APPROVE:         { label: "Draft Approved",      icon: CheckCircle2,tone: "create" },
  DRAFT_CHANGES_REQUESTED: { label: "Draft Changes Requested", icon: RefreshCw, tone: "status" },
  MEETING_BOOKED:        { label: "Meeting Booked",        icon: Phone,         tone: "create" },
  MEETING_CANCELLED:     { label: "Meeting Cancelled",     icon: XCircle,       tone: "delete" },
  MEETING_CONFIRMED:     { label: "Meeting Attended",      icon: CheckCircle2,  tone: "create" },
  MEETING_NO_SHOW:       { label: "Meeting No-Show",       icon: XCircle,       tone: "delete" },
  WHATSAPP_STUB:         { label: "WhatsApp (stub)",       icon: MessageSquare, tone: "status" },
  EMAIL_STUB:            { label: "Email (stub)",          icon: MessageSquare, tone: "status" },
  MEET_LINK_PLACEHOLDER: { label: "Meet Link Generated",   icon: Plus,          tone: "create" },
};

const TONE_STYLES = {
  create:  { wrap: "bg-green-50",   icon: "text-green-600",  ring: "ring-green-200" },
  update:  { wrap: "bg-blue-50",    icon: "text-blue-600",   ring: "ring-blue-200" },
  delete:  { wrap: "bg-red-50",     icon: "text-red-600",    ring: "ring-red-200" },
  status:  { wrap: "bg-amber-50",   icon: "text-amber-600",  ring: "ring-amber-200" },
  upload:  { wrap: "bg-violet-50",  icon: "text-violet-600", ring: "ring-violet-200" },
  payment: { wrap: "bg-violet-50",  icon: "text-violet-600", ring: "ring-violet-200" },
  login:   { wrap: "bg-gray-100",   icon: "text-gray-500",   ring: "ring-gray-200" },
  default: { wrap: "bg-gray-100",   icon: "text-gray-500",   ring: "ring-gray-200" },
};

const ROLE_BADGE = {
  super_admin: { label: "Super Admin", color: "bg-[#6C63FF]/15 text-[#6C63FF]" },
  admin:       { label: "Admin",       color: "bg-purple-100 text-purple-700" },
  ca:          { label: "CA",          color: "bg-blue-100 text-blue-700" },
  user:        { label: "User",        color: "bg-emerald-100 text-emerald-700" },
  system:      { label: "System",      color: "bg-gray-100 text-gray-600" },
};

function metaForAction(action) {
  return (
    LOG_ACTION_META[action] || {
      label: humanize(action),
      icon: Activity,
      tone: "default",
    }
  );
}

function humanize(s) {
  if (!s) return "—";
  return s
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const sec = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60)        return `${sec}s ago`;
  if (sec < 3600)      return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400)     return `${Math.floor(sec / 3600)} h ago`;
  if (sec < 604800)    return `${Math.floor(sec / 86400)} d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
}

function dateGroupKey(ts) {
  if (!ts) return "Unknown";
  const d = new Date(ts);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = (startOfDay(now) - startOfDay(d)) / dayMs;
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function entityLink(log) {
  if (!log.entity_type || !log.entity_id) return null;
  switch (log.entity_type) {
    case "order":      return `/admin/orders/${log.entity_id}`;
    case "user":       return `/admin/users/${log.entity_id}`;
    case "ca_profile": return `/admin/ca-profiles`;
    case "team":       return `/admin/team`;
    default:           return null;
  }
}

function safeJson(v) {
  if (v == null) return null;
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  }
  return v;
}

export default function LogsPanel() {
  const [stats, setStats] = useState(null);
  const [filterCatalog, setFilterCatalog] = useState({
    actions: [],
    entity_types: [],
    actors: [],
  });

  const [filters, setFilters] = useState({
    search: "",
    action: "",
    entity_type: "",
    actor_id: "",
    actor_role: "",
    from: "",
    to: "",
    include_reads: false,
  });
  const [applied, setApplied] = useState({
    search: "",
    action: "",
    entity_type: "",
    actor_id: "",
    actor_role: "",
    from: "",
    to: "",
    include_reads: false,
  });
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ logs: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const [{ data: f }, { data: s }] = await Promise.all([
        API.get("/admin/logs/filters"),
        API.get("/admin/logs/stats"),
      ]);
      setFilterCatalog(f);
      setStats(s);
    } catch {
      // non-fatal — filters just stay empty
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: PAGE_SIZE };
      if (applied.search)        params.search = applied.search;
      if (applied.action)        params.action = applied.action;
      if (applied.entity_type)   params.entity_type = applied.entity_type;
      if (applied.actor_id)      params.actor_id = applied.actor_id;
      if (applied.actor_role)    params.actor_role = applied.actor_role;
      if (applied.from)          params.from = applied.from;
      if (applied.to)            params.to = applied.to + " 23:59:59";
      if (applied.include_reads) params.include_reads = "1";

      const { data } = await API.get("/admin/logs", { params });
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load logs");
    }
    setLoading(false);
  }, [page, applied]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ ...filters });
  };
  const resetFilters = () => {
    const empty = {
      search: "", action: "", entity_type: "", actor_id: "",
      actor_role: "", from: "", to: "", include_reads: false,
    };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (applied.search)        params.set("search", applied.search);
    if (applied.action)        params.set("action", applied.action);
    if (applied.entity_type)   params.set("entity_type", applied.entity_type);
    if (applied.actor_id)      params.set("actor_id", applied.actor_id);
    if (applied.actor_role)    params.set("actor_role", applied.actor_role);
    if (applied.from)          params.set("from", applied.from);
    if (applied.to)            params.set("to", applied.to + " 23:59:59");
    if (applied.include_reads) params.set("include_reads", "1");
    const baseURL = API.defaults.baseURL || "";
    const url = `${baseURL}/admin/logs/export?${params.toString()}`;
    window.open(url, "_blank");
  };

  // Group logs by date header
  const grouped = useMemo(() => {
    const out = [];
    let lastKey = null;
    for (const log of data.logs) {
      const key = dateGroupKey(log.created_at);
      if (key !== lastKey) {
        out.push({ type: "header", key });
        lastKey = key;
      }
      out.push({ type: "row", log });
    }
    return out;
  }, [data.logs]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-[#6C63FF]" />
          <div>
            <h2 className="text-lg font-heading font-bold text-[#1A1A2E]">
              Activity Logs
            </h2>
            <p className="text-xs text-[#9CA3AF] font-body">
              Every meaningful change made by the team — searchable and exportable
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { loadCatalog(); loadLogs(); }}
            className="flex items-center gap-1.5 text-xs font-body text-[#6B7280] hover:text-[#6C63FF] px-3 py-2 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-2 rounded-lg transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI
            label="Today"
            value={stats.today_count}
            sub="events"
            tone="bg-[#6C63FF]/10 text-[#6C63FF]"
          />
          <KPI
            label="Last 7 days"
            value={stats.week_count}
            sub="events"
            tone="bg-sky-50 text-sky-700"
          />
          <KPI
            label="Most active (30d)"
            value={stats.top_actor?.name || "—"}
            sub={stats.top_actor ? `${stats.top_actor.count} events · ${ROLE_BADGE[stats.top_actor.role]?.label || stats.top_actor.role}` : ""}
            tone="bg-violet-50 text-violet-700"
            small
          />
          <KPI
            label="Top action (30d)"
            value={stats.top_action ? metaForAction(stats.top_action.action).label : "—"}
            sub={stats.top_action ? `× ${stats.top_action.count}` : ""}
            tone="bg-emerald-50 text-emerald-700"
            small
          />
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4 relative">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Search
            </label>
            <Search className="absolute left-3 top-[34px] h-3.5 w-3.5 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="Description, actor, entity ID, action…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              className="w-full pl-9 pr-3 py-2 text-sm font-body border border-[#E8E6F8] rounded-lg bg-[#F5F4FB] text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            >
              <option value="">All actions</option>
              {filterCatalog.actions.map((a) => (
                <option key={a.value} value={a.value}>
                  {metaForAction(a.value).label} ({a.count})
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Entity
            </label>
            <select
              value={filters.entity_type}
              onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            >
              <option value="">All entities</option>
              {filterCatalog.entity_types.map((e) => (
                <option key={e.value} value={e.value}>
                  {humanize(e.value)} ({e.count})
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Actor
            </label>
            <select
              value={filters.actor_id}
              onChange={(e) => setFilters((f) => ({ ...f, actor_id: e.target.value }))}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            >
              <option value="">All actors</option>
              {filterCatalog.actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({ROLE_BADGE[a.role]?.label || a.role})
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>

          <div className="md:col-span-3 flex flex-wrap items-center gap-2">
            <RoleChip
              label="All"
              value=""
              current={filters.actor_role}
              onSelect={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
            />
            <RoleChip
              label="Super"
              value="super_admin"
              current={filters.actor_role}
              onSelect={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
            />
            <RoleChip
              label="Admin"
              value="admin"
              current={filters.actor_role}
              onSelect={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
            />
            <RoleChip
              label="CA"
              value="ca"
              current={filters.actor_role}
              onSelect={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
            />
            <RoleChip
              label="System"
              value="system"
              current={filters.actor_role}
              onSelect={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
            />
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] font-body text-[#6B7280] cursor-pointer">
              <input
                type="checkbox"
                checked={filters.include_reads}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, include_reads: e.target.checked }))
                }
                className="accent-[#6C63FF]"
              />
              Include reads
            </label>
          </div>

          <div className="md:col-span-2 flex gap-2 justify-end">
            <button
              onClick={applyFilters}
              className="flex-1 px-3 py-2 bg-[#6C63FF] hover:bg-[#5B52CC] text-white text-xs font-body font-semibold rounded-lg transition-colors"
            >
              Apply
            </button>
            <button
              onClick={resetFilters}
              className="px-3 py-2 border border-[#E8E6F8] text-xs font-body text-[#6B7280] hover:bg-[#F5F4FB] rounded-lg transition-colors"
              title="Reset"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button
              onClick={loadLogs}
              className="mt-2 text-xs text-[#6C63FF] underline font-body"
            >
              Retry
            </button>
          </div>
        ) : data.logs.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
            No activity matches your filters
          </p>
        ) : (
          <div>
            {grouped.map((row, i) =>
              row.type === "header" ? (
                <div
                  key={`h-${row.key}-${i}`}
                  className="px-5 py-2 bg-[#F5F4FB] border-y border-[#E8E6F8] text-[10px] uppercase tracking-wider font-heading font-bold text-[#6B7280]"
                >
                  {row.key}
                </div>
              ) : (
                <LogRow
                  key={row.log.id}
                  log={row.log}
                  expanded={expanded === row.log.id}
                  onToggle={() =>
                    setExpanded((cur) => (cur === row.log.id ? null : row.log.id))
                  }
                />
              )
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8E6F8] bg-[#FAFAFE]">
          <p className="text-xs font-body text-[#6B7280]">
            {data.total === 0
              ? "No results"
              : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, data.total)} of ${data.total.toLocaleString("en-IN")} events`}
          </p>
          {data.pages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="text-xs font-body text-[#6B7280]">
                Page {page} / {data.pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LogRow({ log, expanded, onToggle }) {
  const meta  = metaForAction(log.action);
  const tone  = TONE_STYLES[meta.tone] || TONE_STYLES.default;
  const Icon  = meta.icon;
  const role  = ROLE_BADGE[log.actor_role] || { label: log.actor_role, color: "bg-gray-100 text-gray-600" };
  const link  = entityLink(log);
  const oldV  = safeJson(log.old_value);
  const newV  = safeJson(log.new_value);
  const hasDiff = oldV != null || newV != null;
  const isFailure = log.status === "failure";

  return (
    <div
      className={`px-5 py-3 border-b border-[#E8E6F8] last:border-0 hover:bg-[#FAFAFE] transition-colors cursor-pointer ${expanded ? "bg-[#FAFAFE]" : ""}`}
      onClick={onToggle}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full ${tone.wrap} ring-1 ${tone.ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-4 w-4 ${tone.icon}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-heading font-bold text-[#1A1A2E]">
              {meta.label}
            </span>
            <span className={`text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${role.color}`}>
              {role.label}
            </span>
            {isFailure && (
              <span className="text-[10px] font-body font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                Failure
              </span>
            )}
            <span className="text-[10px] font-mono text-[#9CA3AF]">{log.action}</span>
          </div>

          <p className="text-sm text-[#4A4A60] font-body mt-0.5">
            {log.description || "—"}
          </p>

          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] font-body text-[#9CA3AF]">
            <span>
              by{" "}
              <span className="text-[#1A1A2E] font-medium">
                {log.actor_name || log.actor_email || "system"}
              </span>
            </span>
            {log.entity_type && log.entity_id && (
              <span>
                · entity:{" "}
                {link ? (
                  <Link
                    to={link}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#6C63FF] hover:underline font-mono"
                  >
                    {log.entity_type}#{log.entity_id}
                  </Link>
                ) : (
                  <span className="font-mono">{log.entity_type}#{log.entity_id}</span>
                )}
              </span>
            )}
            {log.ip_address && (
              <span>· ip <span className="font-mono">{log.ip_address}</span></span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-[11px] font-body text-[#9CA3AF]">
            {timeAgo(log.created_at)}
          </span>
          {hasDiff ? (
            expanded ? (
              <ChevronUp className="h-4 w-4 text-[#9CA3AF]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />
            )
          ) : null}
        </div>
      </div>

      {expanded && (
        <div
          className="mt-3 ml-12 pl-4 border-l-2 border-[#E8E6F8] space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-body">
            <DetailField label="Exact Time" value={new Date(log.created_at).toLocaleString("en-IN")} />
            <DetailField label="Actor Email" value={log.actor_email} />
            <DetailField label="IP Address" value={log.ip_address} mono />
            <DetailField label="User Agent" value={log.user_agent} truncate />
          </div>

          {(oldV != null || newV != null) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ValueBlock title="Before" value={oldV} icon={ArrowDown} tone="text-red-500" />
              <ValueBlock title="After"  value={newV} icon={ArrowUp}   tone="text-green-600" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, mono, truncate }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">{label}</p>
      <p
        className={`text-[#1A1A2E] mt-0.5 ${mono ? "font-mono" : ""} ${truncate ? "truncate" : "break-words"}`}
      >
        {value || "—"}
      </p>
    </div>
  );
}

function ValueBlock({ title, value, icon: Icon, tone }) {
  return (
    <div className="bg-[#FAFAFE] border border-[#E8E6F8] rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3 w-3 ${tone}`} />
        <span className="text-[10px] uppercase tracking-wide font-heading font-semibold text-[#6B7280]">
          {title}
        </span>
      </div>
      {value == null ? (
        <p className="text-[11px] text-[#9CA3AF] font-body italic">—</p>
      ) : (
        <pre className="text-[11px] text-[#1A1A2E] font-mono whitespace-pre-wrap break-words leading-snug">
          {typeof value === "object"
            ? JSON.stringify(value, null, 2)
            : String(value)}
        </pre>
      )}
    </div>
  );
}

function RoleChip({ label, value, current, onSelect }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`text-[10px] font-body font-semibold px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-[#6C63FF] text-white border-[#6C63FF]"
          : "bg-white text-[#6B7280] border-[#E8E6F8] hover:bg-[#F5F4FB]"
      }`}
    >
      {label}
    </button>
  );
}

function KPI({ label, value, sub, tone, small }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E6F8] p-4 flex flex-col gap-1">
      <span className={`inline-block text-[10px] font-body font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full self-start ${tone}`}>
        {label}
      </span>
      <p
        className={`font-heading font-bold text-[#1A1A2E] leading-tight ${small ? "text-base truncate" : "text-2xl"}`}
      >
        {value}
      </p>
      {sub && <p className="text-[10px] text-[#9CA3AF] font-body truncate">{sub}</p>}
    </div>
  );
}

